<template>
  <tr>
    <template v-if="$vuetify.breakpoint.mdAndUp">
      <td>
        {{ $dayjs.date(statusDate) }}
        <div class="muted--text">
          {{ $dayjs(statusDate).format("HH:mm:ss") }}
        </div>
      </td>
      <td>
        {{ item.referenceCode }}
        <br />
        {{ $t("common:payment.processor." + item.processor) }}

        <div v-if="item.processor == 'MANUAL'" class="mt-2">
          {{
            $t(
              `common:payment.paymentMethod.${item.paymentMethod}`,
              item.paymentMethod
            )
          }}<br />
          {{ item.externalReference }}
        </div>
      </td>
      <td>
        <PaymentHistoryDetails :payment="item" />
      </td>
      <td>
        <AppShowPaymentStatus
          :value="item"
          with-details
          :loading-icon="item.status == 'PENDING'"
        />
      </td>
      <td>
        <div class="accent--text currency-display">
          <b
            :class="{
              'text-decoration-line-through':
                item.status == 'FULLY_REFUNDED' ||
                item.status == 'PARTIALLY_REFUNDED'
            }"
            >{{ masterCurrency(item.amountCents) }}</b
          >
          <span v-if="item.status == 'PARTIALLY_REFUNDED'"
            >&nbsp;{{
              masterCurrency(item.amountCents - item.amountRefundedCents)
            }}</span
          >
        </div>
      </td>
      <td>
        <AppReceiptLink
          v-for="receipt in item.receipts || []"
          :key="receipt.id"
          :receipt-status="item.receiptsStatus"
          :receipt="receipt"
        />
      </td>
      <td>
        <div v-if="item.status == 'PENDING'">
          <v-tooltip
            v-if="$allowedMutation(item.allowedMutations, 'paymentCancel')"
            bottom
          >
            <template #activator="{ on, attrs }">
              <v-btn
                v-if="$allowedMutation(item.allowedMutations, 'paymentConfirm')"
                icon
                v-bind="attrs"
                :aria-label="
                  $t(
                    'people:ShoppingBasket.TransactionHistory.confirmPaymentButton.alt'
                  )
                "
                small
                :disabled="mutationLoading"
                class="edit-green mb-1"
                v-on="on"
                @click="$emit('confirm', { item: item, evt: $event })"
              >
                <v-icon color="white" small>$iconCheck</v-icon>
                <v-progress-circular
                  v-show="false"
                  color="white"
                  size="21"
                  indeterminate
                />
              </v-btn>
            </template>
            {{
              $t(
                "people:ShoppingBasket.TransactionHistory.ConfirmPaymentButton.label",
                "Confirm payment",
                "Label of confirm payment button in Transaction History tab"
              )
            }}
          </v-tooltip>

          <v-tooltip
            v-if="$allowedMutation(item.allowedMutations, 'paymentCancel')"
            bottom
          >
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                small
                v-bind="attrs"
                :aria-label="
                  $t(
                    'people:ShoppingBasket.TransactionHistory.cancelPaymentButton.alt'
                  )
                "
                class="remove mb-1"
                :disabled="mutationLoading"
                v-on="on"
                @click="$emit('cancel', { item: item, evt: $event })"
              >
                <v-icon color="white" small>$iconTimesCircle</v-icon>
                <v-progress-circular
                  v-show="false"
                  color="white"
                  size="21"
                  indeterminate
                />
              </v-btn>
            </template>
            {{
              $t(
                "people:ShoppingBasket.TransactionHistory.CancelPaymentButton.label",
                "Cancel payment",
                "Label of cancel payment button in Transaction History tab"
              )
            }}
          </v-tooltip>
        </div>
      </td>
    </template>
    <template v-else>
      <!-- What seems to be missing must be in ExpandedPaymentHistory component -->
      <td>
        {{ $dayjs.date(statusDate) }}
        <br />
        <span class="muted--text">
          {{ $dayjs(statusDate).format("HH:mm:ss") }}
        </span>
      </td>
      <td>
        {{ item.referenceCode }}
        <br />
        {{ $t("common:payment.processor." + item.processor) }}

        <div v-if="item.processor == 'MANUAL'" class="mt-2">
          {{
            $t(
              `common:payment.paymentMethod.${item.paymentMethod}`,
              item.paymentMethod
            )
          }}<br />
          {{ item.externalReference }}
        </div>
      </td>
      <td>
        <div class="text-center accent--text currency-display">
          <b>{{ masterCurrency(item.amountCents) }}</b>
        </div>
        <div>
          <AppShowPaymentStatusChip :status="item.status" />
          <v-tooltip v-if="item.status == 'PENDING'" bottom>
            <template #activator="{ on, attrs }">
              <v-progress-circular
                v-bind="attrs"
                indeterminate
                aria-label="refresh"
                color="grey"
                size="14"
                width="2"
                v-on="on"
              />
            </template>
            <span>{{
              $t(
                "people:ShoppingBasketPage.TransactionsHistoryTab.refreshAnimation.tooltip",
                "Refreshes in every 15 seconds"
              )
            }}</span>
          </v-tooltip>
        </div>
      </td>
      <td class="px-0">
        <slot name="actions" />
      </td>
    </template>
  </tr>
</template>
<script>
import { defineComponent, computed } from "vue";
import gql from "fraql";
import { useCurrency } from "@composables/masterdata/config";
import { useDisciplineHelper } from "@composables/masterdata/competitions/disciplines";

import AppShowPaymentStatus from "@AppShows/order/AppShowPaymentStatus.vue";
import AppShowPaymentStatusChip from "@AppShows/AppShowPaymentStatus.vue";
import PaymentHistoryDetails from "./PaymentHistoryDetails.vue";
import AppReceiptLink from "@/components/elements/AppReceiptLink.vue";

export default defineComponent({
  name: "PaymentHistory",
  components: {
    AppShowPaymentStatusChip,
    AppShowPaymentStatus,
    PaymentHistoryDetails,
    AppReceiptLink
  },
  props: {
    item: {
      type: Object,
      required: true
    },
    mutationLoading: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const statusDate = computed(() => {
      switch (props.item.status) {
        case "FULLY_REFUNDED":
        case "PARTIALLY_REFUNDED":
          return props.item.refundedAt;
        case "PAID":
          return props.item.confirmedAt;
        case "PENDING":
        default:
          return props.item.createdAt;
      }
    });

    return {
      ...useCurrency(),
      ...useDisciplineHelper(),
      statusDate
    };
  },
  fragments: {
    productFields: PaymentHistoryDetails.fragments.productFields,
    invoiceFields: gql`
      fragment _ on Invoice {
        ${PaymentHistoryDetails.fragments.invoiceFields}
      }
    `,
    payableFields: gql`
      fragment _ on Payable {
        ${PaymentHistoryDetails.fragments.payableFields}
      }
    `,
    paymentFields: gql`
      fragment _ on Payment {
        id
        processor
        ${PaymentHistoryDetails.fragments.paymentFields}
        paymentMethod
        externalReference
        status
        refundedAt
        confirmedAt
        createdAt
        invoice {
          ${PaymentHistoryDetails.fragments.invoiceFields}
        }
      }
    `
  }
});
</script>
<style scoped>
@media (max-width: 768px) {
  tr td {
    font-size: 12px !important;
  }
}
</style>
